/**
 * Avatar color utilities for generating unique, vibrant colors per user
 */

// Vibrant color palette - 15 distinct, high-saturation colors
const VIBRANT_COLORS = [
  '#E53935', // Red
  '#D81B60', // Pink
  '#8E24AA', // Purple
  '#5E35B1', // Deep Purple
  '#3949AB', // Indigo
  '#1E88E5', // Blue
  '#00ACC1', // Cyan
  '#00897B', // Teal
  '#43A047', // Green
  '#7CB342', // Light Green
  '#C0CA33', // Lime
  '#FDD835', // Yellow
  '#FFB300', // Amber
  '#FB8C00', // Orange
  '#F4511E', // Deep Orange
];

// Role-based pastel colors (existing style)
export const ROLE_COLORS: Record<string, { bg: string; text: string }> = {
  superadmin: { bg: 'bg-purple-500/10', text: 'text-purple-600' },
  primary_admin: { bg: 'bg-rose-500/10', text: 'text-rose-600' },
  owner: { bg: 'bg-amber-500/10', text: 'text-amber-600' },
  admin: { bg: 'bg-blue-500/10', text: 'text-blue-600' },
  manager: { bg: 'bg-green-500/10', text: 'text-green-600' },
  employee: { bg: 'bg-gray-500/10', text: 'text-gray-600' },
  kiosk: { bg: 'bg-cyan-500/10', text: 'text-cyan-600' },
};

/**
 * Generate a consistent color for a user based on their ID
 * Uses a simple hash function to deterministically select from the color palette
 */
export function generateAvatarColor(userId: string): string {
  if (!userId) return VIBRANT_COLORS[0];

  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    const char = userId.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }

  return VIBRANT_COLORS[Math.abs(hash) % VIBRANT_COLORS.length];
}

/**
 * Get initials from first and last name
 */
export function getInitials(firstName?: string | null, lastName?: string | null): string {
  const first = firstName?.[0]?.toUpperCase() || '';
  const last = lastName?.[0]?.toUpperCase() || '';
  return first + last || '?';
}

/**
 * Determine if a color is light (for choosing text color)
 */
function isLightColor(hexColor: string): boolean {
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);

  // Calculate relative luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5;
}

/**
 * Get text color (white or dark) based on background color
 */
export function getContrastTextColor(bgColor: string): string {
  return isLightColor(bgColor) ? '#1f2937' : '#ffffff';
}
